import React, {
  createContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import moment from 'moment'
import API from '../../../services/API'
import AnDatePicker from '../../../components/DateTime/DatePicker'
import CreateDutyModal from './CreateDuty/CreateDutyModel'
import AssignJourneyModal from './AssignJourneyModal'
import AssignShiftModal from './AssignShiftComponent'
import UnassignedShifts from './UnassignedShifts'
import UncoveredShifts from './UncoveredShifts'
import OutstandingJourneys from '../components/OutstandingJourneys'

// MUI
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import Divider from '@mui/material/Divider'
import Chip from '@mui/material/Chip'
import CircularProgress from '@mui/material/CircularProgress'
import Grid from '@mui/material/Grid'

// MUI Icons
import ArrowLeft from '@mui/icons-material/ArrowLeft'
import ArrowRight from '@mui/icons-material/ArrowRight'
import Edit from '@mui/icons-material/Edit'
import Notes from '@mui/icons-material/Notes'
import Visibility from '@mui/icons-material/Visibility'
import AddCircleIcon from '@mui/icons-material/AddCircle'
import LocalShippingIcon from '@mui/icons-material/LocalShipping'
import EventIcon from '@mui/icons-material/Event'
import PeopleIcon from '@mui/icons-material/People'
import DirectionsCarFilledIcon from '@mui/icons-material/DirectionsCarFilled'
import ViewTimelineIcon from '@mui/icons-material/ViewTimeline'
import MonitorHeartOutlinedIcon from '@mui/icons-material/MonitorHeartOutlined'
import BigModal from '../../../components/Modals/BigModal'
import SingleBooking from '../SingleBooking/SingleBooking'
import { useLocation, useNavigate } from 'react-router-dom'
import AddShiftModal from './AddShiftModel'
import MoreVert from '@mui/icons-material/MoreVert'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'

import {
  ArrowDownward,
  KeyboardArrowDownOutlined,
  KeyboardArrowUpOutlined,
  PendingActions,
  Schedule,
} from '@mui/icons-material'
import { Tooltip } from '@mui/material'
import ErrorModal from '../../../components/Modals/Error'
import {
  QuoteStatusChip,
  useBookingQuoteStatuses,
} from '../components/quoteStatus'

const shiftIsCovered = (shift) =>
  !!shift?.assignedTo ||
  !!shift?.assignedSubcontractor ||
  !!shift?.assignedToSubcontractor

const getShiftAssigneeName = (shift) => {
  if (shift?.assignedTo) {
    return `${shift.assignedTo.userAccount?.firstName ?? ''} ${
      shift.assignedTo.userAccount?.lastName ?? ''
    }`.trim()
  }

  if (shift?.assignedSubcontractor) {
    return `${shift.assignedSubcontractor.userAccount?.firstName ?? ''} ${
      shift.assignedSubcontractor.userAccount?.lastName ?? ''
    }`.trim()
  }

  if (shift?.assignedToSubcontractor) return 'Subcontractor'

  return 'Unassigned'
}

// 🔹 Placeholder subcomponents for modularity
const DutyCard = ({ duty, selected, onSelect }) => (
  <Box
    onClick={() => onSelect(duty)}
    sx={{
      p: 1.5,
      mb: 1,
      borderRadius: 2,
      border: selected ? '2px solid' : '1px solid rgba(0,0,0,0.1)',
      borderColor: selected
        ? (theme) => theme.palette.anBlue.main
        : 'transparent',
      bgcolor: selected ? 'rgba(31,61,124,0.05)' : '#fff',
      cursor: 'pointer',
      transition: '0.2s',
      '&:hover': { bgcolor: 'rgba(31,61,124,0.08)' },
    }}
  >
    <Stack direction="row" justifyContent="space-between" alignItems="center">
      <Stack direction="row" spacing={1} alignItems="center">
        <LocalShippingIcon color="anBlue" fontSize="small" />
        <Typography variant="subtitle2">
          {duty.vehicle?.callSign ?? 'No Vehicle'}
        </Typography>
      </Stack>
      <Chip
        label={duty.status}
        size="small"
        sx={{
          textTransform: 'capitalize',
          bgcolor:
            duty.status === 'active'
              ? 'anGreen.main'
              : duty.status === 'completed'
                ? 'anBlue.main'
                : duty.status === 'planned'
                  ? 'anYellow.main'
                  : 'anGrey.main',
          color: '#fff',
          borderRadius: 1,
        }}
      />
    </Stack>
    <Typography variant="body2" color="text.secondary">
      {moment(duty.start).format(
        moment(duty.start).isSame(duty.end, 'day') ? 'HH:mm' : 'ddd Do HH:mm',
      )}{' '}
      -{' '}
      {moment(duty.end).format(
        moment(duty.end).isSame(duty.start, 'day') ? 'HH:mm' : 'ddd Do HH:mm',
      )}
    </Typography>
    <Typography variant="caption" color="text.secondary">
      Crew:{' '}
      {duty?.crewShifts?.reduce((a, x) => (shiftIsCovered(x) ? a + 1 : a), 0) ||
        0}
      /
      {duty?.crewShifts?.length ?? 0} | Routes: {duty.routes?.length || 0} |
      Journeys:{' '}
      {duty.routes.reduce(
        (sum, route) => sum + (route?.journeys?.length || 0),
        0,
      )}
    </Typography>
  </Box>
)

const timeString = (journey) => {
  const patientReadyFrom = journey?.pickup?.when?.patientReadyFrom
  const targetArrivalTime = journey?.dropoff?.when?.targetArrivalTime

  if (patientReadyFrom && !targetArrivalTime) {
    return `Ready from: ${moment(patientReadyFrom).format('HH:mm')}`
  } else if (!patientReadyFrom && targetArrivalTime) {
    return `Arrival: ${moment(targetArrivalTime).format('HH:mm')}`
  } else if (patientReadyFrom && targetArrivalTime) {
    return `${moment(journey.pickup?.when?.patientReadyFrom).format(
      'HH:mm',
    )} → ${moment(journey.dropoff?.when?.targetArrivalTime).format('HH:mm')}`
  }
}

// Pulsing flag for patients on today's plan who have no risk assessment on
// record — prompts the co-ordinator to book an assessment visit or chase one
// before the journey runs.
const RiskAssessmentFlag = ({ patient, riskAssessedPatientIds }) => {
  const patientId = patient?._id || patient
  if (!patientId || !riskAssessedPatientIds) return null
  if (riskAssessedPatientIds.has(String(patientId))) return null

  return (
    <Tooltip title="No risk assessment on record for this patient">
      <MonitorHeartOutlinedIcon
        sx={{
          fontSize: 18,
          color: 'anRed.main',
          borderRadius: '50%',
          '@keyframes raFlagPulse': {
            '0%': { boxShadow: '0 0 0 0 rgba(180,35,24,0.35)' },
            '70%': { boxShadow: '0 0 0 7px rgba(180,35,24,0)' },
            '100%': { boxShadow: '0 0 0 0 rgba(180,35,24,0)' },
          },
          animation: 'raFlagPulse 2.4s ease-out infinite',
        }}
      />
    </Tooltip>
  )
}

const JourneyCard = ({
  journey,
  onAssign,
  quoteStatus,
  riskAssessedPatientIds,
}) => {
  const bookingID = journey?.booking?._id || journey?.booking

  return (
    <Box
      sx={{
        p: 1.2,
        mb: 1,
        borderRadius: 2,
        bgcolor: '#fff',
        boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
      }}
    >
      <Stack
        gap={1}
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
      >
        <Stack direction="row" alignItems="center" gap={0.75}>
          <Typography fontWeight={700}>
            {journey?.booking?.patient?.demographics?.firstName}{' '}
            {journey?.booking?.patient?.demographics?.lastName}
          </Typography>
          <RiskAssessmentFlag
            patient={journey?.booking?.patient}
            riskAssessedPatientIds={riskAssessedPatientIds}
          />
        </Stack>
        <Stack direction="row" alignItems="center" gap={0.5}>
          <QuoteStatusChip status={quoteStatus} warnOnly />
          <Box
            sx={{
              fontSize: 14,
              color: 'contrast.main',
              px: 1,
            }}
          >
            {journey?.booking?.patient?.patientID ?? ''}
          </Box>
          {bookingID && (
            <IconButton
              size="small"
              aria-label="Open booking"
              onClick={() => window.open(`/pts/bookings/${bookingID}`, '_blank')}
              sx={{
                color: 'anBlue.main',
                bgcolor: 'rgba(37,99,235,0.08)',
                '&:hover': { bgcolor: 'rgba(37,99,235,0.16)' },
              }}
            >
              <Visibility fontSize="small" />
            </IconButton>
          )}
        </Stack>
      </Stack>
      <Typography variant="caption" color="text.secondary">
        {journey.pickup?.name || journey.pickup?.town} →{' '}
        {journey.dropoff?.name || journey.dropoff?.town}
      </Typography>
      <Typography variant="subtitle2">{timeString(journey)}</Typography>

      <Button
        variant="outlined"
        color="anBlue"
        size="small"
        fullWidth
        sx={{
          mt: 1,
          textTransform: 'none',
          fontWeight: 600,
          borderRadius: 1.5,
        }}
        onClick={() => onAssign(journey)}
      >
        Assign to Duty
      </Button>
    </Box>
  )
}

// Sharing codes the server sets on a route (helpers/PTS/routeSharing.js)
const ROUTE_WARNING_LABELS = {
  mixedJourneyTypes: 'Mixed journey types',
  mustTravelAloneShared: 'Patient must travel alone',
  noSharedStop: 'No shared pickup or destination',
}

const DutyDetails = ({
  duty,
  reloadDuty,
  setCreateModalOpen,
  setEditDuty,
  linkedJourneys,
  riskAssessedPatientIds,
}) => {
  const [openAssignVehicle, setOpenAssignVehicle] = useState(false)
  const [availableVehicles, setAvailableVehicles] = useState([])
  const [formSelectedVehicle, setFormSelectedVehicle] = useState(null)
  const [selectedJourney, setSelectedJourney] = useState(null)
  const [moveMenuAnchor, setMoveMenuAnchor] = useState(null)
  const [moveMenuJourney, setMoveMenuJourney] = useState(null)
  const [moveMenuRouteIndex, setMoveMenuRouteIndex] = useState(null)
  const [moveMenuJourneyIndex, setMoveMenuJourneyIndex] = useState(null)
  const [moveError, setMoveError] = useState(null)

  const [routeMenuAnchor, setRouteMenuAnchor] = useState(null)
  const [routeMenuIndex, setRouteMenuIndex] = useState(null)
  const [routeSuggestions, setRouteSuggestions] = useState(null)
  const [routeSuggestionLoading, setRouteSuggestionLoading] = useState(false)

  const openRouteMenu = (event, index) => {
    setRouteMenuAnchor(event.currentTarget)
    setRouteMenuIndex(index)
  }

  const closeRouteMenu = () => {
    setRouteMenuAnchor(null)
  }

  const resetRouteMenu = () => {
    setRouteMenuIndex(null)
  }

  const openMoveMenu = (event, journey, routeIndex, journeyIndex) => {
    setMoveMenuAnchor(event.currentTarget)
    setMoveMenuJourney(journey)
    setMoveMenuRouteIndex(routeIndex)
    setMoveMenuJourneyIndex(journeyIndex)
  }

  const closeMoveMenu = () => {
    setMoveMenuAnchor(null)
  }

  const resetMoveMenu = () => {
    setMoveMenuJourney(null)
    setMoveMenuRouteIndex(null)
    setMoveMenuJourneyIndex(null)
  }

  const createAddressString = (address) => {
    const addressString = [
      address?.name,
      address?.addressLine1,
      address?.addressLine2,
      address?.town,
      address?.city,
      address?.postCode,
    ]
      .filter((x) => x)
      .join(', ')
    return addressString
  }

  const loadRouteSuggestions = async () => {
    if (!duty?._id) return
    setRouteSuggestionLoading(true)
    setMoveError(null)
    try {
      const res = await API.get(`/pts/duties/${duty._id}/route-suggestions`)
      if (res.status === 200) {
        setRouteSuggestions(res.data)
      }
    } catch (err) {
      setMoveError(
        err?.response?.data?.message || 'Unable to generate route suggestions',
      )
    } finally {
      setRouteSuggestionLoading(false)
    }
  }

  const applyRouteSuggestions = async () => {
    if (!duty?._id) return
    setRouteSuggestionLoading(true)
    setMoveError(null)
    try {
      const res = await API.post(
        `/pts/duties/${duty._id}/route-suggestions/apply`,
      )
      if (res.status === 200) {
        setRouteSuggestions(null)
        reloadDuty()
      }
    } catch (err) {
      setMoveError(
        err?.response?.data?.message || 'Unable to apply route suggestions',
      )
    } finally {
      setRouteSuggestionLoading(false)
    }
  }

  const moveJourney = async ({
    journeyID,
    sourceRouteIndex,
    sourceJourneyIndex,
    targetRouteIndex,
    targetJourneyIndex,
  }) => {
    try {
      const res = await API.post(
        `/pts/duties/${duty._id}/routes/move-journey`,
        {
          journeyID,
          sourceRouteIndex,
          sourceJourneyIndex,
          targetRouteIndex,
          targetJourneyIndex,
        },
      )

      if (res.status === 200) {
        closeMoveMenu()
        reloadDuty()
      } else if (res.status === 400) {
        setMoveError(res.data?.message)
      }
    } catch (err) {
      setMoveError(err?.response?.data?.message || 'Unable to move journey')
    }
  }

  // The API client resolves on 4xx/5xx, so check the status — a refused
  // move used to do nothing at all
  const changeRoutes = async (request, fallbackMessage) => {
    try {
      const res = await request()
      if (res?.status === 200) {
        reloadDuty()
        return true
      }
      setMoveError(res?.data?.message || res?.data?.error || fallbackMessage)
    } catch (err) {
      setMoveError(err?.response?.data?.message || fallbackMessage)
    }
    return false
  }
  if (!duty) {
    return (
      <Box
        flex={1}
        display="flex"
        alignItems="center"
        justifyContent="center"
        color="text.secondary"
        padding={2}
      >
        <Typography variant="body1">Select a duty to view details</Typography>
      </Box>
    )
  }

  return (
    <Box flex={1} p={2}>
      <Stack spacing={2}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Stack direction="row" gap={1} alignItems="center">
            <Typography variant="h6">
              {duty.vehicle?.callSign ?? 'No Vehicle'}
            </Typography>
            <IconButton
              onClick={() => {
                setCreateModalOpen(true)
                setEditDuty(duty)
              }}
              variant="contained"
            >
              <Edit fontSize="small" />
            </IconButton>
          </Stack>
          <Stack gap={1}>
            <Chip
              label={duty.status}
              sx={{
                textTransform: 'capitalize',
                bgcolor:
                  duty.status === 'Active'
                    ? 'anGreen.main'
                    : duty.status === 'Completed'
                      ? 'anBlue.main'
                      : 'anGrey.main',
                color: '#fff',
                borderRadius: 1,
              }}
              // onClick={(e) => setDutyStatusAnchor(e.currentTarget)}
            />
          </Stack>
          <ErrorModal
            open={!!moveError}
            onClose={() => {
              setMoveError(null)
              closeMoveMenu()
            }}
            message={moveError}
          />
          <Menu
            anchorEl={moveMenuAnchor}
            open={!!moveMenuAnchor}
            onClose={closeMoveMenu}
            TransitionProps={{
              onExited: resetMoveMenu,
            }}
          >
            {duty?.routes?.map((route, targetRouteIndex) => {
              if (targetRouteIndex === moveMenuRouteIndex) return null

              return (
                <MenuItem
                  key={targetRouteIndex}
                  onClick={() =>
                    moveJourney({
                      journeyID: moveMenuJourney._id,
                      sourceRouteIndex: moveMenuRouteIndex,
                      sourceJourneyIndex: moveMenuJourneyIndex,
                      targetRouteIndex,
                      targetJourneyIndex:
                        duty.routes[targetRouteIndex].journeys.length,
                    })
                  }
                >
                  Move to Route {targetRouteIndex + 1}
                </MenuItem>
              )
            })}

            <MenuItem
              onClick={async () => {
                const createRes = await API.post(
                  `/pts/duties/${duty._id}/routes`,
                )
                if (createRes.status === 200) {
                  const updatedDuty = createRes.data
                  const newRouteIndex = updatedDuty.routes.length - 1

                  await moveJourney({
                    journeyID: moveMenuJourney._id,
                    sourceRouteIndex: moveMenuRouteIndex,
                    sourceJourneyIndex: moveMenuJourneyIndex,
                    targetRouteIndex: newRouteIndex,
                    targetJourneyIndex: 0,
                  })
                }
              }}
            >
              Move to New Route
            </MenuItem>

            <MenuItem
              onClick={async () => {
                try {
                  const res = await API.post(
                    `/pts/duties/${duty._id}/routes/remove-journey`,
                    {
                      journeyID: moveMenuJourney._id,
                      routeIndex: moveMenuRouteIndex,
                      journeyIndex: moveMenuJourneyIndex,
                    },
                  )

                  if (res.status === 200) {
                    closeMoveMenu()
                    reloadDuty()
                  }
                } catch (err) {
                  setMoveError(
                    err?.response?.data?.message ||
                      'Unable to remove journey from route',
                  )
                }
              }}
              sx={{ color: 'error.main' }}
            >
              Remove from Duty
            </MenuItem>
          </Menu>
        </Stack>

        <Typography variant="body2" color="text.secondary">
          {moment(duty.start).format('dddd, Do MMM HH:mm')} –{' '}
          {moment(duty.end).format(
            moment(duty.start).isSame(duty.end, 'day')
              ? 'HH:mm'
              : 'dddd, Do MMM HH:mm',
          )}
        </Typography>

        {duty.organisation && (
          <Stack direction="row" gap={0.75} flexWrap="wrap">
            <Chip
              size="small"
              color="info"
              label={`Dedicated to ${duty.organisation?.orgName || 'an organisation'}`}
              sx={{ borderRadius: 1, fontWeight: 700 }}
            />
            {duty.crewSelfDispatch && (
              <Tooltip title="No dispatch — the crew add their own jobs from My PTS Duty">
                <Chip
                  size="small"
                  variant="outlined"
                  color="info"
                  label="Crew add their own jobs"
                  sx={{ borderRadius: 1, fontWeight: 700 }}
                />
              </Tooltip>
            )}
          </Stack>
        )}

        <Divider />

        <Box>
          <Stack direction="row" gap={1} alignItems="center">
            <PeopleIcon fontSize="small" sx={{ color: 'anBlue.main' }} />
            <Typography variant="subtitle2">Crew Shifts</Typography>
          </Stack>

          {duty.crewShifts?.length ? (
            duty.crewShifts.map((s) => (
              <Typography key={s._id} variant="body2">
                {getShiftAssigneeName(s)}{' '}
                ({s.clinicalRole}) {moment(s.start).format('HH:mm')} -{' '}
                {moment(s.end).format('HH:mm')}
              </Typography>
            ))
          ) : (
            <Typography variant="body2" color="text.secondary">
              No crew assigned
            </Typography>
          )}
        </Box>
        <Divider />
        <Stack gap={3}>
          <Stack
            direction={{ xs: 'column', lg: 'row' }}
            justifyContent="space-between"
          >
            <Stack direction="row" gap={1} alignItems="center">
              <DirectionsCarFilledIcon
                fontSize="small"
                sx={{ color: 'contrast.main', lineHeight: 1 }}
              />
              <Typography variant="title3">Routes</Typography>
            </Stack>
            <Stack direction="row" gap={1} flexWrap="wrap">
              {moment(duty.start).isAfter(moment()) && (
                <Button
                  size="small"
                  variant="contained"
                  color="anPurple"
                  disabled={routeSuggestionLoading}
                  onClick={loadRouteSuggestions}
                >
                  Suggest Routes
                </Button>
              )}
              <Button
                size="small"
                variant="outlined"
                onClick={async () => {
                  const res = await API.post(`/pts/duties/${duty._id}/routes`)
                  if (res.status === 200) {
                    reloadDuty()
                  }
                }}
              >
                Add Route
              </Button>
            </Stack>
          </Stack>
          {routeSuggestions && (
            <Box
              sx={{
                p: 1.5,
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'rgba(132,39,106,0.24)',
                bgcolor: 'rgba(132,39,106,0.05)',
              }}
            >
              <Stack gap={1.25}>
                <Stack
                  direction={{ xs: 'column', md: 'row' }}
                  justifyContent="space-between"
                  gap={1}
                >
                  <Box>
                    <Typography variant="subtitle2" fontWeight={800}>
                      Suggested Route Plan
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {routeSuggestions.routes?.length || 0} route
                      {routeSuggestions.routes?.length === 1 ? '' : 's'} based
                      on times, route-sharing rules and vehicle capacity.
                    </Typography>
                  </Box>
                  <Stack direction="row" gap={1}>
                    <Button
                      size="small"
                      onClick={() => setRouteSuggestions(null)}
                    >
                      Dismiss
                    </Button>
                    <Button
                      size="small"
                      variant="contained"
                      color="anPurple"
                      disabled={
                        routeSuggestionLoading || !routeSuggestions.canApply
                      }
                      onClick={applyRouteSuggestions}
                    >
                      Apply
                    </Button>
                  </Stack>
                </Stack>
                {routeSuggestions.warnings?.map((warning) => (
                  <Typography
                    key={warning}
                    variant="caption"
                    color="warning.main"
                  >
                    {warning}
                  </Typography>
                ))}
                <Stack gap={1}>
                  {routeSuggestions.routes?.map((route, routeIndex) => (
                    <Box
                      key={routeIndex}
                      sx={{
                        p: 1,
                        borderRadius: 1.5,
                        bgcolor: '#fff',
                        border: '1px solid rgba(15,23,42,0.08)',
                      }}
                    >
                      <Stack gap={0.75}>
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          gap={1}
                        >
                          <Typography variant="body2" fontWeight={800}>
                            Route {routeIndex + 1}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Score {route.suggestionScore}
                          </Typography>
                        </Stack>
                        {route.journeys?.map((journey, journeyIndex) => (
                          <Typography key={journey._id} variant="caption">
                            {journeyIndex + 1}.{' '}
                            {
                              journey?.booking?.patient?.demographics
                                ?.firstName
                            }{' '}
                            {
                              journey?.booking?.patient?.demographics
                                ?.lastName
                            }{' '}
                            ({timeString(journey) || 'No time'})
                          </Typography>
                        ))}
                        {route.warnings?.map((warning) => (
                          <Typography
                            key={warning}
                            variant="caption"
                            color="warning.main"
                          >
                            {warning}
                          </Typography>
                        ))}
                      </Stack>
                    </Box>
                  ))}
                </Stack>
              </Stack>
            </Box>
          )}
          {duty?.routes?.length ? (
            duty.routes.map((r, index) => (
              <Stack
                key={r._id}
                sx={{
                  borderRadius: 3,
                  border: '1px solid',
                  borderColor: 'rgba(15,23,42,0.08)',
                  bgcolor: 'rgba(255,255,255,0.92)',
                  px: 2.25,
                  py: 2,
                  boxShadow: '0 4px 14px rgba(15,23,42,0.04)',
                }}
                gap={1.5}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ mb: 0.5 }}
                >
                  <Stack direction="row" gap={1} alignItems="center">
                    <Typography variant="title4" sx={{ fontWeight: 700 }}>
                      Route {index + 1} of {duty.routes.length}
                    </Typography>
                    {(r.warnings || [])
                      .filter((warning) => ROUTE_WARNING_LABELS[warning])
                      .map((warning) => (
                        <Chip
                          key={warning}
                          size="small"
                          color="warning"
                          variant="outlined"
                          label={ROUTE_WARNING_LABELS[warning]}
                          sx={{ borderRadius: 1, fontWeight: 600 }}
                        />
                      ))}
                  </Stack>

                  <Stack direction="row" alignItems="center" gap={1}>
                    <Chip
                      size="small"
                      label={`${r.journeys?.length || 0} journey${r.journeys?.length === 1 ? '' : 's'}`}
                      sx={{
                        borderRadius: 1,
                        bgcolor: 'rgba(15,23,42,0.05)',
                        color: 'text.secondary',
                        fontWeight: 600,
                      }}
                    />
                    {r.journeys?.length === 0 && (
                      <Button
                        size="small"
                        color="error"
                        variant="outlined"
                        onClick={async () => {
                          const res = await API.delete(
                            `/pts/duties/${duty._id}/routes/${index}`,
                          )
                          if (res.status === 200) {
                            reloadDuty()
                          }
                        }}
                      >
                        Delete Route
                      </Button>
                    )}

                    <IconButton
                      size="small"
                      onClick={(e) => openRouteMenu(e, index)}
                      sx={{
                        border: '1px solid',
                        borderColor: 'rgba(15,23,42,0.08)',
                        bgcolor: '#fff',
                        '&:hover': {
                          bgcolor: 'rgba(31,61,124,0.06)',
                        },
                      }}
                    >
                      <MoreVert fontSize="small" />
                    </IconButton>
                    <Menu
                      anchorEl={routeMenuAnchor}
                      open={!!routeMenuAnchor}
                      onClose={closeRouteMenu}
                      TransitionProps={{
                        onExited: resetRouteMenu,
                      }}
                    >
                      {routeMenuIndex > 0 && (
                        <MenuItem
                          onClick={async () => {
                            closeRouteMenu()
                            await changeRoutes(
                              () =>
                                API.post(
                                  `/pts/duties/${duty._id}/routes/move-route`,
                                  {
                                    sourceRouteIndex: routeMenuIndex,
                                    targetRouteIndex: routeMenuIndex - 1,
                                  },
                                ),
                              'Unable to move route',
                            )
                          }}
                        >
                          Move Up
                        </MenuItem>
                      )}

                      {routeMenuIndex < duty?.routes?.length - 1 && (
                        <MenuItem
                          onClick={async () => {
                            closeRouteMenu()
                            await changeRoutes(
                              () =>
                                API.post(
                                  `/pts/duties/${duty._id}/routes/move-route`,
                                  {
                                    sourceRouteIndex: routeMenuIndex,
                                    targetRouteIndex: routeMenuIndex + 1,
                                  },
                                ),
                              'Unable to move route',
                            )
                          }}
                        >
                          Move Down
                        </MenuItem>
                      )}

                      {duty?.routes?.[routeMenuIndex]?.journeys?.length ===
                        0 && (
                        <MenuItem
                          sx={{ color: 'error.main' }}
                          onClick={async () => {
                            try {
                              await API.delete(
                                `/pts/duties/${duty._id}/routes/${routeMenuIndex}`,
                              )
                              closeRouteMenu()
                              reloadDuty()
                            } catch (err) {
                              setMoveError(
                                err?.response?.data?.message ||
                                  'Unable to delete route',
                              )
                            }
                          }}
                        >
                          Delete Route
                        </MenuItem>
                      )}
                    </Menu>
                  </Stack>
                </Stack>

                <Stack gap={1.25}>
                  {r.journeys?.map((j, journeyIndex) => (
                    <Box
                      key={j._id}
                      sx={{
                        borderRadius: 2.5,
                        border: '1px solid',
                        borderColor: 'rgba(15,23,42,0.06)',
                        bgcolor: 'rgba(248,250,252,0.95)',
                        px: 1.5,
                        py: 1.25,
                        position: 'relative',
                        overflow: 'hidden',
                      }}
                    >
                      <Stack
                        direction={{ xs: 'column', md: 'row' }}
                        justifyContent="space-between"
                        // alignItems={{ xs: 'flex-start', md: 'center' }}
                        gap={1.5}
                      >
                        <Stack gap={0.75} sx={{ minWidth: 0, flex: 1 }}>
                          <Stack
                            direction="row"
                            gap={1}
                            alignItems="center"
                            flexWrap="wrap"
                          >
                            <Chip
                              size="small"
                              label={timeString(j)}
                              sx={{
                                height: 24,
                                borderRadius: 1,
                                bgcolor: 'rgba(31,61,124,0.10)',
                                color: 'anBlue.main',
                                fontWeight: 700,
                              }}
                            />
                            {!!j?.booking?.patient?.patientID && (
                              <Typography
                                variant="caption"
                                sx={{
                                  px: 0.8,
                                  py: 0.25,
                                  borderRadius: 1,
                                  borderColor: 'contrast.main',
                                  border: 'solid 1px',
                                  bgcolor: 'white',
                                  color: 'contrast.main',
                                  // fontWeight: 600,
                                }}
                              >
                                {j.booking.patient.patientID}
                              </Typography>
                            )}
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 700 }}
                            >
                              {j?.booking?.patient?.demographics?.firstName}{' '}
                              {j?.booking?.patient?.demographics?.lastName}
                            </Typography>
                            <RiskAssessmentFlag
                              patient={j?.booking?.patient}
                              riskAssessedPatientIds={riskAssessedPatientIds}
                            />
                          </Stack>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.75,
                              flexWrap: 'wrap',
                            }}
                          >
                            {createAddressString(j.pickup)}
                            {' → '}
                            {createAddressString(j.dropoff)}
                          </Typography>
                          <Stack
                            direction="row"
                            alignItems="center"
                            flexWrap="wrap"
                            gap={0.5}
                          >
                            {j?.booking?.patient?.mobility?.mustTravelAlone && (
                              <Typography
                                variant="caption"
                                sx={{
                                  px: 0.8,
                                  py: 0.25,
                                  borderRadius: 1,
                                  borderColor: 'anRed.main',
                                  border: 'solid 1px',
                                  bgcolor: 'white',
                                  color: 'anRed.main',
                                }}
                              >
                                Alone
                              </Typography>
                            )}
                            {j?.booking?.patient?.mobility?.isBariatric && (
                              <Typography
                                variant="caption"
                                sx={{
                                  px: 0.8,
                                  py: 0.25,
                                  borderRadius: 1,
                                  borderColor: 'anRed.main',
                                  border: 'solid 1px',
                                  bgcolor: 'white',
                                  color: 'anRed.main',
                                }}
                              >
                                Bariatric
                              </Typography>
                            )}
                          </Stack>
                        </Stack>
                        <Stack gap={1} alignItems="flex-end">
                          <Box
                            sx={{
                              fontSize: 14,
                              color: 'contrast.main',
                              px: 1,
                            }}
                          >
                            {j?.journeyType ?? ''}
                          </Box>
                          <Stack direction="row" gap={0.5}>
                            {journeyIndex > 0 && (
                              <Tooltip title="Move Up">
                                <span>
                                  <IconButton
                                    size="small"
                                    disabled={journeyIndex === 0}
                                    onClick={() =>
                                      changeRoutes(
                                        () =>
                                          API.post(
                                            `/pts/duties/${duty._id}/routes/move-journey`,
                                            {
                                              journeyID: j._id,
                                              sourceRouteIndex: index,
                                              sourceJourneyIndex: journeyIndex,
                                              targetRouteIndex: index,
                                              targetJourneyIndex:
                                                journeyIndex - 1,
                                            },
                                          ),
                                        'Unable to move journey',
                                      )
                                    }
                                  >
                                    <KeyboardArrowUpOutlined fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            )}

                            {journeyIndex + 1 < r.journeys?.length && (
                              <Tooltip title="Move Down">
                                <span>
                                  <IconButton
                                    size="small"
                                    disabled={
                                      journeyIndex === r.journeys.length - 1
                                    }
                                    onClick={() =>
                                      changeRoutes(
                                        () =>
                                          API.post(
                                            `/pts/duties/${duty._id}/routes/move-journey`,
                                            {
                                              journeyID: j._id,
                                              sourceRouteIndex: index,
                                              sourceJourneyIndex: journeyIndex,
                                              targetRouteIndex: index,
                                              targetJourneyIndex:
                                                journeyIndex + 1,
                                            },
                                          ),
                                        'Unable to move journey',
                                      )
                                    }
                                  >
                                    <KeyboardArrowDownOutlined fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            )}
                            <Tooltip title="More actions">
                              <IconButton
                                size="small"
                                onClick={(e) =>
                                  openMoveMenu(e, j, index, journeyIndex)
                                }
                                sx={{
                                  border: '1px solid',
                                  borderColor: 'rgba(15,23,42,0.08)',
                                  bgcolor: '#fff',
                                  '&:hover': {
                                    bgcolor: 'rgba(31,61,124,0.06)',
                                  },
                                }}
                              >
                                <MoreVert fontSize="small" />
                              </IconButton>
                            </Tooltip>

                            <Tooltip title="View Booking">
                              <IconButton
                                onClick={() => {
                                  setSelectedJourney(j)
                                }}
                                size="small"
                                sx={{
                                  border: '1px solid',
                                  borderColor: 'rgba(15,23,42,0.08)',
                                  bgcolor: '#fff',
                                  '&:hover': {
                                    bgcolor: 'rgba(31,61,124,0.06)',
                                  },
                                }}
                              >
                                <Schedule color="anBlue" fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </Stack>
                      </Stack>
                    </Box>
                  ))}
                </Stack>
              </Stack>
            ))
          ) : (
            <Typography>No planned routes for this duty.</Typography>
          )}
          <BigModal
            open={selectedJourney}
            onClose={() => setSelectedJourney(null)}
          >
            <SingleBooking passedBookingID={selectedJourney?.booking?._id} />
          </BigModal>
        </Stack>
        <Divider />
        <Box>
          <Stack direction="row" gap={1} alignItems="center">
            <Notes sx={{ color: 'anBlue.main' }} />
            <Typography variant="subtitle2">Notes</Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary">
            {duty.notes}
          </Typography>
        </Box>
      </Stack>
    </Box>
  )
}

export const PTSDutiesContext = createContext({})

const PTSDutiesPage = () => {
  const location = useLocation()
  const navigate = useNavigate()

  const query = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  )
  const queryDate = query.get('date')
  const queryDuty = query.get('duty')
  const queryEditDuty = query.get('edit')
  const [date, setDate] = useState(moment())
  const [duties, setDuties] = useState([])
  const [selectedDuty, setSelectedDuty] = useState(null)
  const [allJourneys, setAllJourneys] = useState([])
  const [unassignedJourneys, setUnassignedJourneys] = useState([])
  const [riskAssessedPatientIds, setRiskAssessedPatientIds] = useState(
    new Set(),
  )
  const [loading, setLoading] = useState(true)
  const quoteStatuses = useBookingQuoteStatuses(
    unassignedJourneys.map(
      (journey) => journey?.booking?._id || journey?.booking,
    ),
  )
  const [createModalOpen, setCreateModalOpen] = useState(false)
  const [editDuty, setEditDuty] = useState(null)
  const [unassignedShifts, setUnassignedShifts] = useState([])
  const [uncoveredShifts, setUncoveredShifts] = useState([])

  const [assignModalOpen, setAssignModalOpen] = useState(false)
  const [selectedJourney, setSelectedJourney] = useState(null)
  const [assignmentSuggestions, setAssignmentSuggestions] = useState(null)
  const [assignmentSuggestionLoading, setAssignmentSuggestionLoading] =
    useState(false)
  const [assignmentSuggestionError, setAssignmentSuggestionError] =
    useState(null)

  const [viewShift, setViewShift] = useState(null)

  const [shiftsWithoutDuty, setShiftsWithoutDuty] = useState([])

  const normaliseDate = (value) => {
    if (moment.isMoment(value)) return value.clone()
    if (value instanceof Date) return moment(value)
    if (typeof value === 'string') {
      return moment(value, ['YYYYMMDD', moment.ISO_8601], true)
    }
    return moment.invalid()
  }

  const updateDate = (newDate) => {
    const parsed = normaliseDate(newDate)

    if (!parsed.isValid()) return

    const params = new URLSearchParams(location.search)
    params.set('date', parsed.format('YYYYMMDD'))
    params.delete('duty')
    params.delete('edit')

    setDate(parsed)
    navigate({
      pathname: location.pathname,
      search: params.toString(),
    })
  }

  const handleAssignConfirm = async (duty) => {
    try {
      await API.put(`/pts/journeys/${selectedJourney._id}/assign`, {
        dutyId: duty._id,
      })
      setAssignModalOpen(false)
      setSelectedJourney(null)
      // Refresh both lists
      getDuties()
    } catch (err) {
      console.error(err)
    }
  }

  const getUncoveredShifts = () => {
    setLoading(true)
    API.get(
      `/pts/shifts?date=${moment(date).format('YYYYMMDD')}&uncoveredonly=true`,
    )
      .then((res) => {
        if (res.status === 200) {
          setUncoveredShifts(res.data)
        }
      })
      .finally(() => setLoading(false))
  }

  const getDuties = () => {
    API.get(
      `/pts/duties?date=${moment(date).format('YYYYMMDD')}&unassigned=true`,
    ).then((res) => {
      if (res.status === 200) {
        setDuties(res.data.dutyRes)
        setAllJourneys(res.data.journeysRes)
        setRiskAssessedPatientIds(
          new Set(res.data.riskAssessedPatientIds || []),
        )
        setUnassignedJourneys(
          res.data.journeysRes.filter((x) => !x.assignedToDuty),
        )
        if (selectedDuty) {
          const updatedSelected = res.data.dutyRes.find(
            (d) => d._id === selectedDuty._id,
          )
          if (updatedSelected) {
            setSelectedDuty(updatedSelected)
          }
        }
      }
    })
  }

  const reloadDuty = async () => {
    setLoading(true)
    try {
      getDuties()
      getUncoveredShifts()
      setSelectedDuty(null)
    } catch (err) {
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  const didAutoSelectRef = useRef(false)

  useEffect(() => {
    reloadDuty()
  }, [date])

  useEffect(() => {
    if (didAutoSelectRef.current) return
    if (queryDate) {
      setDate(moment(queryDate, 'YYYYMMDD', true))
    }
  }, [queryDate])

  useEffect(() => {
    if (didAutoSelectRef.current) return
    if (!queryDuty) return
    if (!duties?.length) return

    const match = duties.find((d) => String(d._id) === String(queryDuty))
    if (match) {
      setSelectedDuty(match)
      didAutoSelectRef.current = true
    }
    if (match && queryEditDuty === 'true') {
      setEditDuty(match)
      setCreateModalOpen(true)
    }
  }, [queryDuty, queryEditDuty, duties])

  const getUnassignedShifts = () => {
    setLoading(true)
    API.get(
      `/pts/shifts?date=${moment(date).format('YYYYMMDD')}&unassignedonly=true`,
    )
      .then((res) => {
        if (res.status === 200) {
          setUnassignedShifts(res.data)
        }
      })
      .finally(() => setLoading(false))
  }

  const handleAssignClick = (journey) => {
    setSelectedJourney(journey)
    setAssignModalOpen(true)
  }

  const loadAssignmentSuggestions = async () => {
    setAssignmentSuggestionLoading(true)
    setAssignmentSuggestionError(null)
    try {
      const res = await API.get(
        `/pts/unassigned-journey-duty-suggestions?date=${moment(date).format(
          'YYYYMMDD',
        )}`,
      )
      if (res.status === 200) {
        setAssignmentSuggestions(res.data)
      }
    } catch (err) {
      setAssignmentSuggestionError(
        err?.response?.data?.message ||
          'Unable to generate duty suggestions for unassigned journeys',
      )
    } finally {
      setAssignmentSuggestionLoading(false)
    }
  }

  const applyAssignmentSuggestions = async () => {
    setAssignmentSuggestionLoading(true)
    setAssignmentSuggestionError(null)
    try {
      const res = await API.post(
        `/pts/unassigned-journey-duty-suggestions/apply?date=${moment(
          date,
        ).format('YYYYMMDD')}`,
      )
      if (res.status === 200) {
        setAssignmentSuggestions(null)
        getDuties()
      }
    } catch (err) {
      setAssignmentSuggestionError(
        err?.response?.data?.message ||
          'Unable to apply duty suggestions for unassigned journeys',
      )
    } finally {
      setAssignmentSuggestionLoading(false)
    }
  }

  useEffect(() => {
    if (date) {
      getUnassignedShifts()
      getUncoveredShifts()
      setSelectedDuty(null)
    }
  }, [date])

  return (
    <PTSDutiesContext.Provider
      value={{
        loading,
        date,
        getUnassignedShifts,
        unassignedShifts,
        reloadDuty,
        duties,
        setCreateModalOpen,
        getUncoveredShifts,
        uncoveredShifts,
        viewShift,
        setViewShift,
        shiftsWithoutDuty,
        setShiftsWithoutDuty,
      }}
    >
      <OutstandingJourneys
        onJumpToDate={(day) => updateDate(day)}
        sx={{ mb: 2 }}
      />
      <Grid container spacing={2}>
        {/* LEFT PANEL */}
        <Grid item xs={12} lg={3}>
          <Box
            bgcolor="#fff"
            p={2}
            borderRadius={2}
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 600,
                color: 'anBlue.main',
                mb: 1,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <LocalShippingIcon color="anBlue" fontSize="small" />
              Duties
            </Typography>

            <Stack spacing={1.5} mb={2}>
              <Stack
                direction="row"
                gap={1}
                justifyContent="space-between"
                alignItems="center"
              >
                <ArrowLeft
                  fontSize="small"
                  onClick={() => updateDate(moment(date).subtract(1, 'day'))}
                  sx={{ cursor: 'pointer' }}
                />
                <AnDatePicker
                  date={date}
                  onChange={(newDate) => updateDate(newDate)}
                  fullWidth
                />
                <ArrowRight
                  onClick={() => updateDate(moment(date).add(1, 'day'))}
                  fontSize="small"
                  sx={{ cursor: 'pointer' }}
                />
              </Stack>
              <Stack spacing={0.5}>
                <Button
                  color="anGreen"
                  startIcon={<AddCircleIcon />}
                  onClick={() => setCreateModalOpen(true)}
                  variant="contained"
                  fullWidth
                  size="small"
                >
                  Create Duty
                </Button>
                <Button
                  color="contrast"
                  startIcon={<Schedule />}
                  onClick={() => navigate('/pts/bookings/new-booking')}
                  variant="contained"
                  size="small"
                  fullWidth
                >
                  Create Booking
                </Button>
                <Button
                  color="anPurple"
                  startIcon={<PendingActions />}
                  onClick={() => navigate('/pts/control')}
                  variant="contained"
                  fullWidth
                  size="small"
                >
                  View Control Room
                </Button>
                <Button
                  color="anBlue"
                  startIcon={<ViewTimelineIcon />}
                  onClick={() =>
                    navigate(
                      `/pts/planning/timeline?date=${moment(date).format('YYYYMMDD')}`,
                    )
                  }
                  variant="outlined"
                  fullWidth
                  size="small"
                >
                  Vehicle Timeline
                </Button>
                <Button
                  color="anGreen"
                  startIcon={<PeopleIcon />}
                  onClick={() =>
                    navigate(
                      `/pts/planning/patient-timeline?date=${moment(date).format('YYYYMMDD')}`,
                    )
                  }
                  variant="outlined"
                  fullWidth
                  size="small"
                >
                  Patient Timeline
                </Button>
              </Stack>
            </Stack>

            <Divider sx={{ mb: 2 }} />

            <Box
              sx={{
                flex: 1,
                overflowY: 'auto',
              }}
            >
              {loading ? (
                <Box textAlign="center" mt={4}>
                  <CircularProgress size={24} />
                </Box>
              ) : duties.length ? (
                duties.map((d) => (
                  <DutyCard
                    key={d._id}
                    duty={d}
                    selected={selectedDuty?._id === d._id}
                    onSelect={setSelectedDuty}
                  />
                ))
              ) : (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  textAlign="center"
                  mt={2}
                >
                  No duties for this date
                </Typography>
              )}
            </Box>

            <CreateDutyModal
              open={createModalOpen}
              onClose={() => {
                setCreateModalOpen(false)
                setEditDuty(null)
                reloadDuty()
              }}
              onSave={() => {
                setCreateModalOpen(false)
                setEditDuty(null)
                reloadDuty()
              }}
              editDuty={editDuty}
              selectedDate={date}
            />
          </Box>
        </Grid>

        {/* CENTRE PANEL */}
        <Grid
          item
          xs={12}
          lg={
            uncoveredShifts?.length ||
            unassignedShifts?.length ||
            unassignedJourneys?.length
              ? 6
              : 9
          }
        >
          <Box
            flex={1}
            bgcolor="#fff"
            borderRadius={2}
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
              height: '100%',
            }}
          >
            <DutyDetails
              duty={selectedDuty}
              reloadDuty={reloadDuty}
              setCreateModalOpen={setCreateModalOpen}
              setEditDuty={setEditDuty}
              linkedJourneys={allJourneys.filter(
                (x) => String(x?.assignedDuty) === String(selectedDuty?._id),
              )}
              riskAssessedPatientIds={riskAssessedPatientIds}
            />
          </Box>
        </Grid>

        {/* RIGHT PANEL */}
        {Boolean(
          uncoveredShifts?.length ||
          unassignedShifts?.length ||
          unassignedJourneys?.length,
        ) && (
          <Grid item xs={12} lg={3}>
            <Stack gap={1}>
              {uncoveredShifts?.length > 0 && <UncoveredShifts />}
              {unassignedShifts?.length > 0 && <UnassignedShifts />}
              {unassignedJourneys?.length > 0 && (
                <Box
                  bgcolor="#fff"
                  p={2}
                  borderRadius={2}
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                    <EventIcon color="anBlue" />
                    <Typography
                      variant="subtitle1"
                      fontWeight={600}
                      color="anBlue.main"
                    >
                      Unassigned Journeys
                    </Typography>
                  </Stack>

                  <Stack gap={1} mb={2}>
                    <Button
                      size="small"
                      variant="contained"
                      color="anPurple"
                      disabled={
                        assignmentSuggestionLoading ||
                        !unassignedJourneys.length ||
                        !duties.length
                      }
                      onClick={loadAssignmentSuggestions}
                      fullWidth
                    >
                      Suggest Routes
                    </Button>
                    {assignmentSuggestionError && (
                      <Typography variant="caption" color="error.main">
                        {assignmentSuggestionError}
                      </Typography>
                    )}
                    {assignmentSuggestions && (
                      <Box
                        sx={{
                          p: 1.25,
                          borderRadius: 2,
                          border: '1px solid',
                          borderColor: 'rgba(132,39,106,0.24)',
                          bgcolor: 'rgba(132,39,106,0.05)',
                        }}
                      >
                        <Stack gap={1}>
                          <Stack direction="row" justifyContent="space-between">
                            <Typography variant="subtitle2" fontWeight={800}>
                              Duty suggestions
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {assignmentSuggestions.suggestions?.length || 0}{' '}
                              placed
                            </Typography>
                          </Stack>
                          {assignmentSuggestions.warnings?.map((warning) => (
                            <Typography
                              key={warning}
                              variant="caption"
                              color="warning.main"
                            >
                              {warning}
                            </Typography>
                          ))}
                          <Stack gap={0.75}>
                            {assignmentSuggestions.suggestions
                              ?.slice(0, 6)
                              .map((suggestion) => (
                                <Box
                                  key={suggestion.journey._id}
                                  sx={{
                                    p: 0.75,
                                    borderRadius: 1.5,
                                    bgcolor: '#fff',
                                    border: '1px solid rgba(15,23,42,0.08)',
                                  }}
                                >
                                  <Typography variant="caption" fontWeight={800}>
                                    {
                                      suggestion?.journey?.booking?.patient
                                        ?.demographics?.firstName
                                    }{' '}
                                    {
                                      suggestion?.journey?.booking?.patient
                                        ?.demographics?.lastName
                                    }
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    display="block"
                                  >
                                    {suggestion?.duty?.vehicle?.callSign ||
                                      'No Vehicle'}{' '}
                                    {suggestion.createsNewRoute
                                      ? 'new route'
                                      : `route ${suggestion.routeIndex + 1}`}{' '}
                                    · score {suggestion.score}
                                  </Typography>
                                </Box>
                              ))}
                            {(assignmentSuggestions.suggestions?.length || 0) >
                              6 && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                +
                                {assignmentSuggestions.suggestions.length - 6}{' '}
                                more
                              </Typography>
                            )}
                          </Stack>
                          <Stack direction="row" gap={1}>
                            <Button
                              size="small"
                              onClick={() => setAssignmentSuggestions(null)}
                            >
                              Dismiss
                            </Button>
                            <Button
                              size="small"
                              variant="contained"
                              color="anPurple"
                              disabled={
                                assignmentSuggestionLoading ||
                                !assignmentSuggestions.suggestions?.length
                              }
                              onClick={applyAssignmentSuggestions}
                            >
                              Apply
                            </Button>
                          </Stack>
                        </Stack>
                      </Box>
                    )}
                  </Stack>

                  <Box sx={{ flex: 1, overflowY: 'auto', pr: 1 }}>
                    {loading ? (
                      <Box textAlign="center" mt={4}>
                        <CircularProgress size={24} />
                      </Box>
                    ) : unassignedJourneys.length ? (
                      unassignedJourneys.map((j) => (
                        <JourneyCard
                          key={j._id}
                          journey={j}
                          quoteStatus={
                            quoteStatuses[
                              String(j?.booking?._id || j?.booking)
                            ]
                          }
                          onAssign={handleAssignClick}
                          riskAssessedPatientIds={riskAssessedPatientIds}
                        />
                      ))
                    ) : (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        textAlign="center"
                        mt={2}
                      >
                        No unassigned journeys for this date
                      </Typography>
                    )}

                    <AssignJourneyModal
                      open={assignModalOpen}
                      onClose={() => setAssignModalOpen(false)}
                      duties={duties}
                      onConfirm={handleAssignConfirm}
                      setOpenCreateDuty={setCreateModalOpen}
                    />
                  </Box>
                </Box>
              )}
            </Stack>
          </Grid>
        )}
      </Grid>
      <AddShiftModal
        open={!!viewShift}
        onClose={() => setViewShift(null)}
        existingShift={viewShift}
        duty={selectedDuty}
        onCreate={() => {
          getUnassignedShifts()
          getUncoveredShifts()
        }}
      />
    </PTSDutiesContext.Provider>
  )
}

export default PTSDutiesPage
